import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import {
  Assignment,
  LocalShipping,
  CalendarToday,
  Assessment,
  ErrorOutline,
  CheckCircle,
  Description,
  Timer,
  FileDownload,
} from '@mui/icons-material';
import { Shipment, BoxStatus, ShipmentStatus } from 'src/types/Shipment.types';

type Props = {
  shipment: Shipment;
  onDownloadFile?: (url: string, fileName?: string) => Promise<void> | void;
  downloadingFile?: boolean;
};

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch { return '—'; }
};

const safePercent = (num: number, denom: number) =>
  denom > 0 ? Math.min(100, Math.max(0, Math.round((num / denom) * 100))) : 0;

const statusMeta: Record<number, { label: string; color: 'default'|'info'|'warning'|'success'|'error' }> = {
  [ShipmentStatus.Processing]: { label: 'Processing', color: 'info' },
  [ShipmentStatus.Expected]:   { label: 'Expected',   color: 'warning' },
  [ShipmentStatus.InReception]:{ label: 'In Reception', color: 'info' },
  [ShipmentStatus.Received]:   { label: 'Received',   color: 'success' },
  [ShipmentStatus.Analyzing]:  { label: 'Analyzing',  color: 'info' },
  [ShipmentStatus.Analyzed]:   { label: 'Analyzed',   color: 'success' },
  [ShipmentStatus.Completing]: { label: 'Completing', color: 'info' },
  [ShipmentStatus.Completed]:  { label: 'Completed',  color: 'success' },
  [ShipmentStatus.Allocated]:  { label: 'Allocated',  color: 'info' },
  [ShipmentStatus.Cancelled]:  { label: 'Cancelled',  color: 'error' },
};

const StatCard = ({
  title, value, subtitle, tone = 'default'
}: {
  title: string; value: string | number; subtitle?: string; tone?: 'primary'|'success'|'warning'|'info'|'error'|'default'
}) => (
  <Card
    sx={{
      borderRadius: 2,
      boxShadow: 'none',
      border: '1px solid',
      borderColor: `${tone}.200`,
      bgcolor: `${tone}.lighter`,
      '&:hover': { boxShadow: 1, borderColor: `${tone}.main` },
      transition: 'all .2s ease'
    }}
  >
    <CardContent sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: .4 }}>
        {title.toUpperCase()}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: .5 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const Row = ({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) => (
  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1.2 }}>
    {icon}
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: .5 }}>
        {label.toUpperCase()}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 500, mt: .4 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const Badge = ({ show, color, label, icon }: { show?: boolean; color: 'warning'|'error'|'success'|'info', label: string, icon: React.ReactNode }) => {
  if (!show) return null;
  return (
    <Chip
      icon={icon as any}
      label={label}
      color={color}
      size="small"
      sx={{ fontWeight: 600 }}
    />
  );
};

const ShipmentGeneralInfoTab: React.FC<Props> = ({ shipment, onDownloadFile, downloadingFile }) => {
  const progressPct = useMemo(
    () => safePercent(shipment?.receivedBoxCount ?? 0, shipment?.expectedBoxCount ?? 0),
    [shipment?.receivedBoxCount, shipment?.expectedBoxCount]
  );

  const expectedItems = (shipment as any).totalExpectedItems ?? shipment?.stats?.totalExpectedItems ?? 0;
  const receivedItems = (shipment as any).totalReceivedItems ?? shipment?.stats?.totalReceivedItems ?? 0;

  const expectedQty = (shipment as any).discrepancySummary?.totalExpectedQuantity
    ?? shipment?.stats?.totalExpectedQuantity ?? 0;
  const receivedQty = (shipment as any).discrepancySummary?.totalReceivedQuantity
    ?? shipment?.stats?.totalReceivedQuantity ?? 0;

  const posCount = (shipment as any).discrepancySummary?.positiveDiscrepancyCount ?? (shipment as any).positiveDiscrepancyCount ?? 0;
  const negCount = (shipment as any).discrepancySummary?.negativeDiscrepancyCount ?? (shipment as any).negativeDiscrepancyCount ?? 0;

  const hasDisc = !!shipment?.hasDiscrepancy;

  const status = statusMeta[shipment.status] ?? { label: String(shipment.statusName || shipment.status), color: 'default' as const };

  return (
    <Box sx={{ p: 3 }}>
      {/* Top: title + badges */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Assignment sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            General Information
          </Typography>
          <Chip
            label={status.label}
            color={status.color}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Badge
            show={shipment.isOverdue}
            color="warning"
            label="Overdue"
            icon={<Timer />}
          />
          <Badge
            show={hasDisc}
            color="error"
            label="Discrepancies"
            icon={<ErrorOutline />}
          />
          <Badge
            show={!hasDisc && shipment.status >= ShipmentStatus.Analyzed}
            color="success"
            label="No Discrepancies"
            icon={<CheckCircle />}
          />
        </Stack>
      </Stack>

      {/* Quick KPIs */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Expected Boxes" value={shipment.expectedBoxCount ?? 0} tone="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Received Boxes" value={shipment.receivedBoxCount ?? 0} tone="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Expected Items" value={expectedItems} tone="info" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Received Items" value={receivedItems} tone="info" />
        </Grid>
      </Grid>

      {/* Progress */}
      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              BOX RECEPTION PROGRESS
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {shipment.receivedBoxCount ?? 0} / {shipment.expectedBoxCount ?? 0} ({progressPct}%)
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={progressPct} />
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Left column: core facts */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 2 }}>
            <CardHeader
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocalShipping sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Shipment Facts
                  </Typography>
                </Stack>
              }
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Row
                    label="Supplier"
                    value={shipment.supplier?.name || shipment.supplierName || '—'}
                    icon={<Assessment fontSize="small" color="primary" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Row
                    label="Reference"
                    value={<span style={{ fontFamily: 'monospace' }}>{shipment.refNumber}</span>}
                    icon={<Description fontSize="small" color="primary" />}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Row
                    label="Expected Date"
                    value={formatDate(shipment.expectedDate)}
                    icon={<CalendarToday fontSize="small" color="primary" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Row
                    label="Actual Arrival"
                    value={formatDate(shipment.actualArrivalDate)}
                    icon={<CalendarToday fontSize="small" color="primary" />}
                  />
                </Grid>

               
                <Grid item xs={12} sm={6}>
                  <Row
                    label="Box Discrepancy"
                    value={
                      <Tooltip title="ReceivedBoxCount - ExpectedBoxCount">
                        <span style={{ fontWeight: 600, color: (shipment.boxDiscrepancy ?? 0) < 0 ? '#d32f2f' : '#2e7d32' }}>
                          {(shipment.boxDiscrepancy ?? 0) > 0 ? '+' : ''}{shipment.boxDiscrepancy ?? 0}
                        </span>
                      </Tooltip>
                    }
                    icon={<ErrorOutline fontSize="small" color="primary" />}
                  />
                </Grid>
              </Grid>

              {/* Divider */}
              <Divider sx={{ my: 2 }} />

              {/* Discrepancy summary */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Discrepancy Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Expected Qty" value={expectedQty} tone="default" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Received Qty" value={receivedQty} tone="default" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Positive Count" value={posCount} tone="success" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Negative Count" value={negCount} tone="error" />
                </Grid>
              </Grid>

              {/* Notes */}
              {(shipment.notes ?? '').trim() !== '' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: .5 }}>
                    Notes
                  </Typography>
                  <Typography variant="body2">{shipment.notes}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right column: files & flags */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            {(shipment.implementationFile || shipment.report) && (
              <Card sx={{ borderRadius: 2 }}>
                <CardHeader
                  title={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Files</Typography>}
                  sx={{ pb: 0 }}
                />
                <CardContent sx={{ pt: 1 }}>
                  <Stack spacing={1.2}>
                    {!!shipment.implementationFile && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownload />}
                        disabled={!!downloadingFile}
                        onClick={() => onDownloadFile?.(shipment.implementationFile!, `${shipment.refNumber}-implementation.xlsx`)}
                        sx={{ textTransform: 'none', borderRadius: 1 }}
                      >
                        Implementation
                      </Button>
                    )}
                    {!!shipment.report && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<FileDownload />}
                        disabled={!!downloadingFile}
                        onClick={() => onDownloadFile?.(shipment.report!, `${shipment.refNumber}-discrepancy-report.xlsx`)}
                        sx={{ textTransform: 'none', borderRadius: 1 }}
                      >
                        Discrepancy Report
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {(shipment.isOverdue || shipment.report) && (
              <Card sx={{ borderRadius: 2 }}>
                <CardHeader
                  title={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Status</Typography>}
                  sx={{ pb: 0 }}
                />
                <CardContent sx={{ pt: 1 }}>
                  <Stack spacing={1.2}>
                    {shipment.isOverdue && (
                      <Chip color="warning" icon={<Timer />} label="Shipment Overdue" size="small" sx={{ fontWeight: 600 }} />
                    )}
                    {shipment.report && (
                      <Chip color="error" icon={<ErrorOutline />} label="Discrepancies Detected" size="small" sx={{ fontWeight: 600 }} />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ShipmentGeneralInfoTab;
